import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useDevice } from './DeviceContext';
import { useTenant } from './TenantContext';

interface DeviceUnlockContextType {
  isUnlockRequired: boolean;
  isLocked: boolean;
  idleTimeoutMinutes: number;
  unlockWithPin: (pin: string) => Promise<boolean>;
}

export const DEFAULT_IDLE_TIMEOUT_MINUTES = 60;
export const ACTIVITY_THROTTLE_MS = 10_000;

const DeviceUnlockContext = createContext<DeviceUnlockContextType>({
  isUnlockRequired: false,
  isLocked: false,
  idleTimeoutMinutes: DEFAULT_IDLE_TIMEOUT_MINUTES,
  unlockWithPin: async () => false,
});

export const normalizeIdleTimeoutMinutes = (value: unknown, fallback: number): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(5, Math.min(24 * 60, Math.floor(parsed)));
};

export const shouldTabletLock = (lastActivityAtMs: number, idleTimeoutMinutes: number, nowMs: number): boolean => {
  const idleMs = idleTimeoutMinutes * 60_000;
  return nowMs - lastActivityAtMs >= idleMs;
};

export const shouldPersistActivity = (lastWriteAtMs: number, nowMs: number, throttleMs = ACTIVITY_THROTTLE_MS): boolean => {
  return nowMs - lastWriteAtMs >= throttleMs;
};

export const DeviceUnlockProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, verifyPin } = useAuth();
  const { deviceType, isRegistered, deviceId } = useDevice();
  const { tenant } = useTenant();
  const [isLocked, setIsLocked] = useState(false);
  const lastActivityAtRef = useRef<number>(Date.now());
  const lastWriteAtRef = useRef<number>(0);

  const idleTimeoutMinutes = useMemo(() => {
    const tenantTimeout = (tenant as any)?.security?.tabletIdleUnlockMinutes;
    const envTimeout = import.meta.env.VITE_TABLET_UNLOCK_IDLE_MINUTES;
    return normalizeIdleTimeoutMinutes(tenantTimeout ?? envTimeout, DEFAULT_IDLE_TIMEOUT_MINUTES);
  }, [tenant]);

  const isUnlockRequired = !!user && isRegistered && deviceType === 'ambulance_tablet';

  const storageKey = useMemo(() => {
    const tenantId = user?.tenantId || tenant.tenantId || 'default';
    const userId = user?.uid || 'anonymous';
    return `aspis_unlock_activity:${tenantId}:${deviceId}:${userId}`;
  }, [deviceId, tenant.tenantId, user?.tenantId, user?.uid]);

  const persistLastActivity = useCallback((timestamp: number) => {
    try {
      localStorage.setItem(storageKey, String(timestamp));
    } catch {
      // Ignore storage failures; lock policy still works in memory.
    }
  }, [storageKey]);

  const evaluateLockState = useCallback(() => {
    if (!isUnlockRequired) {
      setIsLocked(false);
      return;
    }

    setIsLocked(shouldTabletLock(lastActivityAtRef.current, idleTimeoutMinutes, Date.now()));
  }, [idleTimeoutMinutes, isUnlockRequired]);

  useEffect(() => {
    if (!isUnlockRequired) {
      setIsLocked(false);
      return;
    }

    try {
      const stored = Number(localStorage.getItem(storageKey) || '0');
      if (Number.isFinite(stored) && stored > 0) {
        lastActivityAtRef.current = stored;
      } else {
        const now = Date.now();
        lastActivityAtRef.current = now;
        persistLastActivity(now);
      }
    } catch {
      lastActivityAtRef.current = Date.now();
    }

    evaluateLockState();
  }, [evaluateLockState, isUnlockRequired, persistLastActivity, storageKey]);

  useEffect(() => {
    if (!isUnlockRequired) return;

    const onActivity = () => {
      if (isLocked) return;

      const now = Date.now();
      if (!shouldPersistActivity(lastWriteAtRef.current, now, ACTIVITY_THROTTLE_MS)) return;

      lastActivityAtRef.current = now;
      lastWriteAtRef.current = now;
      persistLastActivity(now);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        evaluateLockState();
      }
    };

    const events: Array<keyof WindowEventMap> = ['click', 'keydown', 'touchstart', 'mousemove'];
    events.forEach((eventName) => window.addEventListener(eventName, onActivity, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);

    const intervalId = window.setInterval(() => {
      evaluateLockState();
    }, 30_000);

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, onActivity));
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(intervalId);
    };
  }, [evaluateLockState, isLocked, isUnlockRequired, persistLastActivity]);

  const unlockWithPin = useCallback(async (pin: string): Promise<boolean> => {
    if (!isUnlockRequired) return true;

    const verified = await verifyPin(pin);
    if (!verified) {
      return false;
    }

    const now = Date.now();
    lastActivityAtRef.current = now;
    lastWriteAtRef.current = now;
    persistLastActivity(now);
    setIsLocked(false);
    return true;
  }, [isUnlockRequired, persistLastActivity, verifyPin]);

  return (
    <DeviceUnlockContext.Provider
      value={{
        isUnlockRequired,
        isLocked,
        idleTimeoutMinutes,
        unlockWithPin,
      }}
    >
      {children}
    </DeviceUnlockContext.Provider>
  );
};

export const useDeviceUnlock = (): DeviceUnlockContextType => useContext(DeviceUnlockContext);
